import { formatRawCurrency } from '@app/components/price/currency-formatter.ts';
import { useCurrency } from '@app/components/price/use-currency.ts';
import Spinner from '@app/components/spinner.tsx';
import SuperButton from '@app/components/super-button.tsx';
import {
  BranchItemFragment,
  OnlineOrderBranchConfigurationItemFragment,
  OnlineOrderDeliveryFeeCalculationStatus,
  OnlineOrderItemFragment,
} from '@app/graphql/types/graphql.ts';
import AlertError from '@app/module/error/alert-error';
import ApplicationErrorView from '@app/module/error/application-error-view.tsx';
import CartSideBarPreview from '@app/page/online-order/fill-contact-info/component/cart-side-bar-preview.tsx';
import { CartSideBarPreviewMode } from '@app/page/online-order/fill-contact-info/utils/cart-side-bar-preview-mode-enum.ts';
import { analyticOrderPreview } from '@app/page/online-order/product-picker/utils/online-order-analytic.ts';
import ContactAndAddressInfo from '@app/page/online-order/review/component/contact-and-address-info.tsx';
import { useOnlineOrderReviewCancel } from '@app/page/online-order/review/logic/use-online-order-review-cancel.ts';
import { useOnlineOrderReviewComplete } from '@app/page/online-order/review/logic/use-online-order-review-complete.ts';
import { captureException } from '@sentry/react';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import Card from '@app/components/card/card.tsx';
import CardHeader from '@app/components/card/card-header.tsx';
import ErrorDialog from '@app/page/online-order/fill-contact-info/component/error-dialog.tsx';

export default function OnlineOrderReviewPage(props: {
  onlineOrder: OnlineOrderItemFragment;
  branch: BranchItemFragment;
  configuration: OnlineOrderBranchConfigurationItemFragment;
}) {
  const { onlineOrder } = props;
  const currency = useCurrency();
  const [cancelReview, { error: cancelError, loading: cancelLoading }] = useOnlineOrderReviewCancel();
  const [completeReview, { error: completeError, loading: completeLoading }] = useOnlineOrderReviewComplete();
  const { t } = useTranslate();

  const [errorDialog, setErrorDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    actionText?: string;
    contactPhone?: string | null;
  } | null>(null);

  const backToFillAddress = () => {
    cancelReview({
      variables: {
        onlineOrderID: props.onlineOrder.id,
      },
    }).catch(captureException);
  };

  const submitHandler = () => {
    completeReview({
      variables: {
        onlineOrderID: props.onlineOrder.id,
      },
    })
      .then(() => {
        let discountPrice;
        if (props.onlineOrder.pickupDiscount) {
          discountPrice = Number(formatRawCurrency(props.onlineOrder.pickupDiscount, currency).value);
        }

        if (props.onlineOrder.deliveryDiscount) {
          discountPrice = Number(formatRawCurrency(props.onlineOrder.deliveryDiscount, currency).value);
        }

        analyticOrderPreview({
          deliveryMode: props.onlineOrder.deliveryMode,
          timeMode: props.onlineOrder.expectedTime ? 'Order for later' : 'Now',
          totalPrice: props.onlineOrder.totalPrice
            ? Number(formatRawCurrency(props.onlineOrder.totalPrice, currency).value)
            : null,
          deliveryFee: props.onlineOrder.deliveryPrice
            ? Number(formatRawCurrency(props.onlineOrder.deliveryPrice, currency).value)
            : null,
          discountPrice: discountPrice,
        });

        localStorage.setItem('DONE_STAGE_FIRST_ACCESS', Date.now().toString());
      })
      .catch(captureException);
  };

  const validateForOrderButton = () => {
    if (onlineOrder.deliveryFeeCalculationStatus === OnlineOrderDeliveryFeeCalculationStatus.OutOfRange) {
      return true;
    }
    return false;
  };

  // Show error dialog when delivery is out of range
  useEffect(() => {
    if (onlineOrder.deliveryFeeCalculationStatus === OnlineOrderDeliveryFeeCalculationStatus.OutOfRange) {
      setErrorDialog({
        isOpen: true,
        title: t('delivery-information.out-of-range.title', 'Delivery address is too far'),
        message: t(
          'delivery-information.out-of-range.description',
          'Please go back to the previous page to edit your address or choose Pickup.'
        ),
        actionText: t('delivery-information.out-of-range.action', 'Edit Address'),
        contactPhone: props.branch.contactPhone,
      });
    }
  }, [onlineOrder.deliveryFeeCalculationStatus, t, props.branch.contactPhone]);

  return (
    <div className="order-review sm:p-8">
      <div className="order-review__content max-w-[1000px] space-y-4 sm:basis-2/3">
        {props.configuration.paymentMethods.length == 0 && (
          <AlertError
            title={t('empty-payment-method-error')}
            message={t('empty-payment-method-error-message', 'Payment method is not available')}
          />
        )}
        <ApplicationErrorView error={cancelError || completeError} />
        <BackStateCard
          loading={cancelLoading || completeLoading}
          backToFillAddress={backToFillAddress}
          t={(key: string, defaultValue: string) => {
            return t(key, defaultValue);
          }}
        />

        {/*<BusinessCard branch={props.branch} />*/}

        <div className="order-review__summary">
          <CartSideBarPreview order={props.onlineOrder} editable={false} mode={CartSideBarPreviewMode.Full} />
        </div>

        <ContactAndAddressInfo
          onlineOrder={props.onlineOrder}
          branch={props.branch}
          configuration={props.configuration}
        />

        <div className="order-review__actions px-1 sm:px-0">
          <SuperButton disabled={validateForOrderButton()} onClick={submitHandler}>
            {cancelLoading || completeLoading ? (
              <Spinner />
            ) : (
              <span>{t('online-order.review-page.review-side-bar.button.order', 'Order')}</span>
            )}
          </SuperButton>
        </div>
      </div>

      {/* Error Dialog for Out of Range */}
      {errorDialog?.isOpen && (
        <ErrorDialog
          isOpen={errorDialog.isOpen}
          onClose={() => setErrorDialog(null)}
          error={{
            title: errorDialog.title,
            message: errorDialog.message,
            actionText: errorDialog.actionText,
            contactPhone: errorDialog.contactPhone,
            onAction: () => {
              setErrorDialog(null);
              backToFillAddress();
            },
          }}
        />
      )}
    </div>
  );
}

const BackStateCard = (props: {
  backToFillAddress: () => void;
  t: (key: string, defaultValue: string) => string;
  loading: boolean;
}) => {
  return (
    <Card className="card">
      <CardHeader
        title={!props.loading ? props.t('online-order.review-page.back-to-edit-address', 'Back to edit address') : ''}
        handleBackButtonClick={props.backToFillAddress}
        withBackButton={true}
      >
        {props.loading && <Spinner />}
      </CardHeader>
    </Card>
  );
};
